import { z } from 'zod';

import { timestamp } from '~/services/types.ts';

function normalizeComponentUnit(value: unknown) {
  if (typeof value !== 'string')
    return value;
  const normalized = value.toLowerCase();
  if (normalized === 'ppmmol')
    return 'PpmMol';
  if (normalized === 'micrometer')
    return 'Micrometer';
  if (normalized === 'ppmww')
    return 'PpmWw';
  return value;
}

function normalizeComponentInputType(value: unknown) {
  if (typeof value !== 'string')
    return value;
  const normalized = value.toLowerCase();
  if (normalized === 'numeric')
    return 'Numeric';
  if (normalized === 'boolean')
    return 'Boolean';
  return value;
}

function normalizeQualifier(value: unknown) {
  if (typeof value !== 'string')
    return value;
  const normalized = value.toLowerCase();
  if (normalized === 'exact')
    return 'Exact';
  if (normalized === 'notdetected')
    return 'NotDetected';
  if (normalized === 'detected')
    return 'Detected';
  return value;
}

export const componentUnitSchema = z.preprocess(
  normalizeComponentUnit,
  z.enum(['PpmMol', 'Micrometer', 'PpmWw']),
);
export type ComponentUnit = z.infer<typeof componentUnitSchema>;

export const componentInputTypeSchema = z.preprocess(
  normalizeComponentInputType,
  z.enum(['Numeric', 'Boolean']),
);
export type ComponentInputType = z.infer<typeof componentInputTypeSchema>;

export const componentQualifierSchema = z.preprocess(
  normalizeQualifier,
  z.enum(['Exact', 'NotDetected', 'Detected']),
);
export type ComponentQualifier = z.infer<typeof componentQualifierSchema>;

export const basisStatusEnum = z.enum(['Active', 'Superseded']);
export type BasisStatus = z.infer<typeof basisStatusEnum>;

export const componentSchema = z.object({
  id: z.string(),
  key: z.string(),
  code: z.string(),
  name: z.string(),
  unit: componentUnitSchema,
  inputType: componentInputTypeSchema,
});
export type Component = z.infer<typeof componentSchema>;

export const projectCompositionBasisItemSchema = z.object({
  id: z.string(),
  basisId: z.string(),
  componentId: z.string(),
  qualifier: componentQualifierSchema.nullable(),
  value: z.number().nullable(),
  note: z.string().nullable(),
});
export type ProjectCompositionBasisItem = z.infer<typeof projectCompositionBasisItemSchema>;

export const projectCompositionBasisSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  summary: z.string(),
  documentReference: z.string().nullable(),
  validFrom: timestamp,
  validTo: timestamp.nullable(),
  status: basisStatusEnum,
  items: projectCompositionBasisItemSchema.array(),
});
export type ProjectCompositionBasis = z.infer<typeof projectCompositionBasisSchema>;

export const projectCompositionGeneralNoteSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  note: z.string().nullable(),
  updatedAt: timestamp,
  updatedByUserId: z.string().nullable(),
});
export type ProjectCompositionGeneralNote = z.infer<typeof projectCompositionGeneralNoteSchema>;

export const projectCompositionSchemaItemSchema = z.object({
  id: z.string(),
  schemaId: z.string(),
  componentId: z.string(),
  component: componentSchema,
  displayOrder: z.number(),
  maxValue: z.number().nullable(),
  detectionLimit: z.number().nullable(),
  quantificationLimit: z.number().nullable(),
});
export type ProjectCompositionSchemaItem = z.infer<typeof projectCompositionSchemaItemSchema>;

export const projectCompositionSchemaSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  items: projectCompositionSchemaItemSchema.array(),
});
export type ProjectCompositionSchema = z.infer<typeof projectCompositionSchemaSchema>;

export type UpdateSchemaItemLimitsRequest = {
  detectionLimit: number | null;
  quantificationLimit: number | null;
};

export type BasisItemRequest = {
  componentKey: string;
  qualifier: ComponentQualifier | null;
  value: number | null;
  note: string | null;
};

export type CreateBasisRequest = {
  summary: string;
  documentReference: string | null;
  validFrom: string | null;
  validTo: string | null;
  items: BasisItemRequest[];
};

export type UpdateBasisRequest = {
  summary: string | null;
  documentReference: string | null;
  validFrom: string | null;
  validTo: string | null;
  items: BasisItemRequest[] | null;
};
